import { all, call, put, takeLatest, select } from 'redux-saga/effects';
import i18n from 'i18next';
import { processRequest } from '../../../services/Api';
import { historyPush, historyReplace } from '../../../services/History';
import { questionModalActionTypes } from './Constants';
import { getZendeskUrl } from '../../../services/Helper';
import { uploadFileHelper } from '../../../pagesNext/shared/Sagas';
import { getQuestionId } from './Selectors';
import * as questionModalActions from './Actions';
import * as notificationActions from '../../notification/Actions';
import * as qaActions from '../../../pagesNext/qA/Actions';
import * as errorHandlerActions from '../../errorHandler/Actions';

export default function* () {
	yield all([
		takeLatest(
			questionModalActionTypes.GET_QUESTION_AVAILABLE_SUBJECTS,
			handleAvailableSubjectsRequest,
		),
		takeLatest(
			questionModalActionTypes.GET_QUESTION_AVAILABLE_TOPICS,
			handleAvailableTopicsRequest,
		),
		takeLatest(
			questionModalActionTypes.UPLOAD_QUESTION_ATTACHMENT_REQUEST,
			handleUploadImageRequest,
		),
		takeLatest(questionModalActionTypes.CREATE_QUESTION_REQUEST, createQuestionRequest),
		takeLatest(questionModalActionTypes.UPDATE_QUESTION_REQUEST, updateQuestionRequest),
	]);
}

function createNotificationAction(notification_status, edit) {
	const isDeletedStatus =
		notification_status === 'deleted_after_creation' ||
		notification_status === 'deleted_after_edit';

	return notificationActions.createNotification(
		i18n.t(
			`modals.question_modal.notifications.${
				isDeletedStatus ? notification_status : edit ? 'edited' : notification_status
			}`,
		),
		null,
		isDeletedStatus,
		isDeletedStatus ? 180000 : 5000,
		true,
		null,
		isDeletedStatus ? { title: i18n.t('general.rules_btn') } : undefined,
		isDeletedStatus
			? () => window.open(getZendeskUrl('zendesk_removed_question_or_answer_url'), '_blank')
			: undefined,
	);
}

export function* handleAvailableSubjectsRequest() {
	try {
		const { data } = yield call(processRequest, 'subjects');

		yield put(questionModalActions.getAvailableSubjectsSuccess(data));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(questionModalActions.getAvailableSubjectsError());
	}
}

export function* handleAvailableTopicsRequest({ payload }) {
	try {
		const {
			data: { subject_id },
		} = payload || {};
		const { data } = yield call(processRequest, `public/subjects/${subject_id}/topics`);
		yield put(questionModalActions.getAvailableTopicsSuccess({ chapters: data.results }));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'ignore'));
		yield put(questionModalActions.getAvailableTopicsError());
	}
}

export function* handleUploadImageRequest({ payload }) {
	try {
		const { file } = payload || {};

		const { linkToFile, fileName } = yield call(uploadFileHelper, 'qna/questions', file);

		yield put(questionModalActions.uploadImageSuccess(linkToFile, fileName));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(questionModalActions.uploadImageError());
	}
}

export function* createQuestionRequest({ payload }) {
	try {
		const { data: formData } = payload || {};

		const { data } = yield call(processRequest, 'qna/questions', 'POST', formData);

		yield put(questionModalActions.createQuestionSuccess());

		const { qna_question: { id, title, notification_status } = {} } = data || {};

		if (notification_status !== 'deleted_after_creation') {
			yield call(historyPush, `/question/${id}/${encodeURIComponent(title)}`);
		}

		yield put(createNotificationAction(notification_status));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(questionModalActions.createQuestionError());
	}
}

export function* updateQuestionRequest({ payload }) {
	try {
		const { data: formData } = payload || {};
		const questionId = yield select(getQuestionId);

		const { data } = yield call(
			processRequest,
			`qna/questions/${questionId}`,
			'PATCH',
			formData,
		);

		const { qna_question: { id, title, notification_status } = {} } = data || {};

		yield put(qaActions.getQuestionDetails({ questionId }));
		yield put(questionModalActions.createQuestionSuccess());
		yield call(historyReplace, `/question/${id}/${encodeURIComponent(title)}`);
		yield put(createNotificationAction(notification_status, true));
	} catch (e) {
		yield put(errorHandlerActions.handleError(e, 'notification'));
		yield put(questionModalActions.createQuestionError());
	}
}
